import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  TrendingUp, 
  TrendingDown, 
  Minus,
  DollarSign,
  AlertTriangle,
  Package,
  Users,
  RefreshCw,
  BarChart3,
  PiggyBank,
  ShoppingCart
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell
} from 'recharts';
import SearchableSelect from '../components/SearchableSelect';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PriceAnalyticsPage = ({ user }) => {
  const [overview, setOverview] = useState(null);
  const [trends, setTrends] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [skus, setSkus] = useState([]);
  const [selectedSku, setSelectedSku] = useState(null);
  const [skuAnalytics, setSkuAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [overviewRes, trendsRes, vendorsRes, alertsRes, skusRes] = await Promise.all([
        axios.get(`${API}/price-analytics/overview`, { headers }),
        axios.get(`${API}/price-analytics/trends?months=12`, { headers }),
        axios.get(`${API}/price-analytics/vendor-performance`, { headers }),
        axios.get(`${API}/price-analytics/alerts`, { headers }),
        axios.get(`${API}/inventory`, { headers })
      ]);
      
      setOverview(overviewRes.data);
      setTrends(trendsRes.data.trends);
      setVendors(vendorsRes.data.vendors);
      setAlerts(alertsRes.data.alerts);
      setSkus(skusRes.data);
    } catch (error) {
      toast.error('Failed to fetch analytics data');
    } finally {
      setLoading(false);
    }
  };

  const fetchSkuAnalytics = async (skuId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/price-analytics/sku/${skuId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSkuAnalytics(response.data);
      setSelectedSku(skuId);
    } catch (error) {
      toast.error('Failed to fetch SKU analytics');
    }
  };

  const recalculateBaselines = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/price-analytics/recalculate-baselines`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error('Failed to recalculate baselines');
    }
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'increasing': return <TrendingUp className="h-4 w-4 text-red-500" />;
      case 'decreasing': return <TrendingDown className="h-4 w-4 text-green-500" />;
      default: return <Minus className="h-4 w-4 text-slate-500" />;
    }
  };

  const getVendorRatingBadge = (rating) => {
    const config = {
      excellent: 'bg-green-100 text-green-800',
      good: 'bg-blue-100 text-blue-800',
      average: 'bg-yellow-100 text-yellow-800',
      poor: 'bg-red-100 text-red-800'
    };
    return <Badge className={config[rating] || config.average}>{rating}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Price Analytics</h1>
          <p className="text-slate-600 mt-1">Historical price analysis and vendor performance</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          {user?.is_admin && (
            <Button onClick={recalculateBaselines} className="bg-slate-900 hover:bg-slate-800">
              Recalculate Baselines
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b pb-2">
        {['overview', 'vendors', 'alerts', 'sku-details'].map((tab) => (
          <Button
            key={tab}
            variant={activeTab === tab ? 'default' : 'ghost'}
            onClick={() => setActiveTab(tab)}
            className={activeTab === tab ? 'bg-slate-900' : ''}
          >
            {tab === 'overview' && <BarChart3 className="h-4 w-4 mr-2" />}
            {tab === 'vendors' && <Users className="h-4 w-4 mr-2" />}
            {tab === 'alerts' && <AlertTriangle className="h-4 w-4 mr-2" />}
            {tab === 'sku-details' && <Package className="h-4 w-4 mr-2" />}
            {tab.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
          </Button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && overview && (
        <div className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Total Spent</p>
                    <p className="text-2xl font-bold">₹{overview.total_spent.toLocaleString()}</p>
                  </div>
                  <DollarSign className="h-8 w-8 text-slate-400" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Total Purchases</p>
                    <p className="text-2xl font-bold">{overview.total_purchases}</p>
                  </div>
                  <ShoppingCart className="h-8 w-8 text-slate-400" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Price Alerts</p>
                    <p className="text-2xl font-bold text-amber-600">{overview.active_price_alerts}</p>
                  </div>
                  <AlertTriangle className="h-8 w-8 text-amber-400" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Potential Savings</p>
                    <p className="text-2xl font-bold text-green-600">₹{overview.potential_savings.toLocaleString()}</p>
                  </div>
                  <PiggyBank className="h-8 w-8 text-green-400" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Monthly Trends Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Monthly Spending Trend</CardTitle>
            </CardHeader>
            <CardContent>
              {trends.length === 0 ? (
                <p className="text-slate-500 text-center py-8">No purchase data available</p>
              ) : (
                <div className="space-y-4">
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={trends} margin={{ top: 5, right: 20, left: 10, bottom: 30 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" angle={-35} textAnchor="end" tick={{ fontSize: 11 }} />
                      <YAxis tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(v) => [`₹${v.toLocaleString()}`, 'Total Spent']} />
                      <Bar dataKey="total_spent" fill="#1e293b" radius={[3,3,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Month</TableHead>
                        <TableHead className="text-right">Total Spent</TableHead>
                        <TableHead className="text-right">Purchases</TableHead>
                        <TableHead className="text-right">Avg/Purchase</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {trends.slice(-6).reverse().map((t) => (
                        <TableRow key={t.month}>
                          <TableCell>{t.month}</TableCell>
                          <TableCell className="text-right font-medium">₹{t.total_spent.toLocaleString()}</TableCell>
                          <TableCell className="text-right">{t.purchase_count}</TableCell>
                          <TableCell className="text-right">₹{t.avg_per_purchase.toLocaleString()}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Top Spending Parts */}
          <Card>
            <CardHeader>
              <CardTitle>Top Spending Parts</CardTitle>
            </CardHeader>
            <CardContent>
              {overview.top_spending_parts.length === 0 ? (
                <p className="text-slate-500 text-center py-4">No data available</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={overview.top_spending_parts.slice(0, 6)}
                        dataKey="total_spent"
                        nameKey="part_name"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        label={({ name, percent }) => `${name.slice(0, 12)}… ${(percent * 100).toFixed(0)}%`}
                      >
                        {overview.top_spending_parts.slice(0, 6).map((_, i) => (
                          <Cell key={i} fill={['#1e293b','#334155','#475569','#64748b','#94a3b8','#cbd5e1'][i]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v) => `₹${v.toLocaleString()}`} />
                    </PieChart>
                  </ResponsiveContainer>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Part Name</TableHead>
                        <TableHead className="text-right">Total Spent</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {overview.top_spending_parts.map((p, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-medium">{p.part_name}</TableCell>
                          <TableCell className="text-right">₹{p.total_spent.toLocaleString()}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Vendors Tab */}
      {activeTab === 'vendors' && (
        <Card>
          <CardHeader>
            <CardTitle>Vendor Performance</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Vendor</TableHead>
                  <TableHead className="text-right">Total Spent</TableHead>
                  <TableHead className="text-right">Purchases</TableHead>
                  <TableHead className="text-right">Unique Parts</TableHead>
                  <TableHead className="text-right">Price vs Baseline</TableHead>
                  <TableHead>Rating</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {vendors.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                      No vendor data available
                    </TableCell>
                  </TableRow>
                ) : (
                  vendors.map((v) => (
                    <TableRow key={v.vendor_name}>
                      <TableCell className="font-medium">{v.vendor_name}</TableCell>
                      <TableCell className="text-right">₹{v.total_spent.toLocaleString()}</TableCell>
                      <TableCell className="text-right">{v.purchase_count}</TableCell>
                      <TableCell className="text-right">{v.unique_skus}</TableCell>
                      <TableCell className="text-right">
                        <span className={v.avg_price_vs_baseline < 0 ? 'text-green-600' : v.avg_price_vs_baseline > 10 ? 'text-red-600' : ''}>
                          {v.avg_price_vs_baseline > 0 ? '+' : ''}{v.avg_price_vs_baseline}%
                        </span>
                      </TableCell>
                      <TableCell>{getVendorRatingBadge(v.rating)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Alerts Tab */}
      {activeTab === 'alerts' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Price Alerts ({alerts.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {alerts.length === 0 ? (
              <p className="text-slate-500 text-center py-8">No active price alerts</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>PR #</TableHead>
                    <TableHead>Part Name</TableHead>
                    <TableHead className="text-right">Actual Price</TableHead>
                    <TableHead className="text-right">Historical Avg</TableHead>
                    <TableHead className="text-right">Difference</TableHead>
                    <TableHead>Vendor</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {alerts.map((a) => (
                    <TableRow key={a.pr_id} className="bg-amber-50">
                      <TableCell className="font-mono text-sm">{a.pr_number}</TableCell>
                      <TableCell className="font-medium">{a.part_name}</TableCell>
                      <TableCell className="text-right text-red-600 font-medium">
                        ₹{a.actual_price.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right">
                        ₹{a.historical_avg.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right text-red-600">
                        +{a.difference_pct}%
                      </TableCell>
                      <TableCell>{a.vendor_name}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{a.status}</Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* SKU Details Tab */}
      {activeTab === 'sku-details' && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Select Part for Analysis</CardTitle>
            </CardHeader>
            <CardContent>
              <SearchableSelect
                value={selectedSku || ''}
                onValueChange={fetchSkuAnalytics}
                options={skus.map(s => ({ value: s.id, label: `${s.particulars} (${s.part_number})` }))}
                placeholder="Select a part to analyze"
                className="max-w-md"
              />
            </CardContent>
          </Card>

          {skuAnalytics && (
            <>
              {/* SKU Stats */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-sm text-slate-600">Average Price</p>
                    <p className="text-2xl font-bold">₹{skuAnalytics.statistics.average_price}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-sm text-slate-600">Lowest Price</p>
                    <p className="text-2xl font-bold text-green-600">₹{skuAnalytics.statistics.min_price}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-sm text-slate-600">Highest Price</p>
                    <p className="text-2xl font-bold text-red-600">₹{skuAnalytics.statistics.max_price}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-600">Price Trend</p>
                        <p className="text-lg font-bold capitalize">{skuAnalytics.price_trend}</p>
                      </div>
                      {getTrendIcon(skuAnalytics.price_trend)}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Vendor Comparison */}
              <Card>
                <CardHeader>
                  <CardTitle>Vendor Comparison for {skuAnalytics.sku.particulars}</CardTitle>
                </CardHeader>
                <CardContent>
                  {skuAnalytics.vendor_comparison.length === 0 ? (
                    <p className="text-slate-500 text-center py-4">No vendor data available</p>
                  ) : (
                    <div className="space-y-4">
                      <ResponsiveContainer width="100%" height={200}>
                        <BarChart data={skuAnalytics.vendor_comparison} margin={{ top: 5, right: 20, left: 10, bottom: 40 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="vendor_name" angle={-25} textAnchor="end" tick={{ fontSize: 11 }} />
                          <YAxis tickFormatter={v => `₹${v}`} tick={{ fontSize: 11 }} />
                          <Tooltip formatter={(v) => [`₹${v}`, 'Avg Price']} />
                          <Bar dataKey="average_price" fill="#1e293b" radius={[3,3,0,0]} name="Avg Price" />
                        </BarChart>
                      </ResponsiveContainer>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Vendor</TableHead>
                            <TableHead className="text-right">Avg Price</TableHead>
                            <TableHead className="text-right">Min Price</TableHead>
                            <TableHead className="text-right">Max Price</TableHead>
                            <TableHead className="text-right">Purchases</TableHead>
                            <TableHead>Recommendation</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {skuAnalytics.vendor_comparison.map((v, idx) => (
                            <TableRow key={v.vendor_name} className={idx === 0 ? 'bg-green-50' : ''}>
                              <TableCell className="font-medium">{v.vendor_name}</TableCell>
                              <TableCell className="text-right">₹{v.average_price}</TableCell>
                              <TableCell className="text-right text-green-600">₹{v.min_price}</TableCell>
                              <TableCell className="text-right text-red-600">₹{v.max_price}</TableCell>
                              <TableCell className="text-right">{v.purchase_count}</TableCell>
                              <TableCell>
                                {idx === 0 && <Badge className="bg-green-100 text-green-800">Best Price</Badge>}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Price History */}
              <Card>
                <CardHeader>
                  <CardTitle>Price History</CardTitle>
                </CardHeader>
                <CardContent>
                  {skuAnalytics.price_history.length === 0 ? (
                    <p className="text-slate-500 text-center py-4">No price history available</p>
                  ) : (
                    <div className="space-y-4">
                      <ResponsiveContainer width="100%" height={200}>
                        <LineChart data={skuAnalytics.price_history} margin={{ top: 5, right: 20, left: 10, bottom: 30 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="month" angle={-35} textAnchor="end" tick={{ fontSize: 11 }} />
                          <YAxis tickFormatter={v => `₹${v}`} tick={{ fontSize: 11 }} />
                          <Tooltip formatter={(v) => [`₹${v}`, 'Avg Price']} />
                          <Line type="monotone" dataKey="avg_price" stroke="#1e293b" strokeWidth={2} dot={{ r: 4 }} />
                        </LineChart>
                      </ResponsiveContainer>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Month</TableHead>
                            <TableHead className="text-right">Avg Price</TableHead>
                            <TableHead className="text-right">Quantity</TableHead>
                            <TableHead className="text-right">Purchases</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {skuAnalytics.price_history.map((h) => (
                            <TableRow key={h.month}>
                              <TableCell>{h.month}</TableCell>
                              <TableCell className="text-right">₹{h.avg_price}</TableCell>
                              <TableCell className="text-right">{h.total_quantity}</TableCell>
                              <TableCell className="text-right">{h.purchase_count}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default PriceAnalyticsPage;
